import { useAuth } from "@dl/web-core-js/auth";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Button, cn, Dialog, MultiSelect, Select, SeriesBarChart } from "@dl/web-core-js/ui";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { billedOf, figuresFor, money as pickMoney, units as pickUnits } from "../../api/figures";
import { marginBreakdown, type MarginBreakdown } from "../../api/margin";
import { useSalesBasis } from "../../sales/basis";
import { NexpimView } from "../../views";
import {
  fxRate,
  ReportingClient,
  type Currency,
  type Period,
} from "../../api/reporting";
import { Movement } from "./figures";
import {
  iso,
  percent,
  ratio,
  withCommas,
  type DrillTarget,
  type PeriodColumn,
} from "./periods";

type PeriodType = "quarter" | "month" | "week";

interface NamedPeriod {
  key: string;
  label: string;
  period: Period;
}

/**
 * The period rows the breakdown grid runs over, ported from
 * `SalesAnalysisGridLayout` — quarters, calendar months, or the ISO-ish weeks
 * `LocalDateUtil.weeksOfYear` cuts (first Monday on or after Jan 1, last week
 * truncated at Dec 31).
 */
function periodsForYear(year: number, type: PeriodType): NamedPeriod[] {
  if (type === "quarter") {
    return [1, 2, 3, 4].map((quarter) => ({
      key: `q${quarter}`,
      label: `Q${quarter} ${year}`,
      period: {
        from: iso(new Date(year, (quarter - 1) * 3, 1)),
        to: iso(new Date(year, quarter * 3, 0)),
      },
    }));
  }

  if (type === "week") {
    const weeks: NamedPeriod[] = [];
    const monday = new Date(year, 0, 1);
    monday.setDate(monday.getDate() + ((8 - (monday.getDay() || 7)) % 7));
    let index = 1;
    while (monday.getFullYear() === year) {
      const sunday = new Date(monday);
      sunday.setDate(sunday.getDate() + 6);
      const end = sunday.getFullYear() > year ? new Date(year, 11, 31) : sunday;
      weeks.push({
        key: `w${index}`,
        label: `W${String(index).padStart(2, "0")} · ${iso(monday)}`,
        period: { from: iso(monday), to: iso(end) },
      });
      monday.setDate(monday.getDate() + 7);
      index += 1;
    }
    return weeks;
  }

  return Array.from({ length: 12 }, (_, month) => ({
    key: `m${month}`,
    label: new Date(year, month, 1).toLocaleString(undefined, { month: "long", year: "numeric" }),
    period: { from: iso(new Date(year, month, 1)), to: iso(new Date(year, month + 1, 0)) },
  }));
}

/** One period's full cost breakdown — a row of the analysis grid. */
interface BreakdownRow extends MarginBreakdown {
  key: string;
  label: string;
  hasSales: boolean;
  grossMarginPercent: number;
}

/** One SKU's money and units for each comparison window. */
interface ProductRow {
  id: number;
  no: string;
  description: string;
  values: Record<string, { money: number; units: number }>;
}

const EMPTY_PRODUCT_CELL = { money: 0, units: 0 };

/**
 * One client's story, in the three views the Vaadin `SalesAnalysisPane` had:
 * the monthly chart, the period-by-period cost breakdown, and the per-SKU grid
 * over the same comparison windows as the page behind it.
 */
/**
 * One client's figures, opened from either sales screen.
 *
 * Takes a `DrillTarget` rather than a grid row: it only ever needed an id, a
 * name, a currency and whether the row is a currency total, and narrowing the
 * contract is what lets the margin page open the same dialog without importing
 * the analysis page's row model.
 */
export function ClientDrillDown({
  client: row,
  columns,
  onClose,
}: {
  client: DrillTarget;
  columns: PeriodColumn[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { http, hasView } = useAuth();
  const { basis, scope, count } = useSalesBasis();
  const client = useMemo(() => new ReportingClient(http), [http]);

  const currentYear = new Date().getFullYear();
  const [years, setYears] = useState<string[]>([String(currentYear)]);
  const [mode, setMode] = useState<"money" | "units">("money");
  const [tab, setTab] = useState<"chart" | "breakdown" | "products" | "margin">("chart");
  const [breakdownYear, setBreakdownYear] = useState(String(currentYear));
  const [periodType, setPeriodType] = useState<PeriodType>("month");

  // The synthetic total rows carry a currency instead of a client id, which is
  // the same branch `SalesAnalysisPane` took on `client.getId() <= 0`.
  const aggregate = useMemo(() => {
    const currency = (row.currency || "CAD") as Currency;
    return (period: Period) =>
      row.synthetic
        ? client.invoicedForCurrency(currency, period)
        : client.aggregateForClient(row.id, period);
  }, [client, row.currency, row.id, row.synthetic]);

  // The margin tab answers "where did the NCM go", which is the margin page's
  // question asked about one client. It is gated on that page's own view, so
  // an account without it simply does not see the tab.
  const canSeeMargin = hasView(NexpimView.SALES_CLIENT_MARGIN);

  const rateQuery = useQuery({
    queryKey: ["globalfx"],
    queryFn: () => client.globalFx(),
    staleTime: 30 * 60_000,
  });
  const rate = fxRate(rateQuery.data, (row.currency || null) as Currency | null);

  const chartQuery = useQuery({
    queryKey: ["sales-analysis", "client-monthly", row.id, years.join(","), basis, scope],
    queryFn: async () => {
      const perYear = await Promise.all(
        years.map(async (year) => {
          const months = await Promise.all(
            Array.from({ length: 12 }, (_, month) => {
              const last = new Date(Number(year), month + 1, 0).getDate();
              const pad = String(month + 1).padStart(2, "0");
              return aggregate({
                from: `${year}-${pad}-01`,
                to: `${year}-${pad}-${String(last).padStart(2, "0")}`,
              });
            }),
          );
          return { year, months };
        }),
      );

      return Array.from({ length: 12 }, (_, month) => {
        const entry: Record<string, unknown> = {
          month: new Date(2000, month, 1).toLocaleString(undefined, { month: "short" }),
        };
        for (const { year, months } of perYear) {
          const monthly = months[month]!;
          const figures = figuresFor(monthly, scope);
          entry[`y${year}_money`] = pickMoney(figures, basis);
          entry[`y${year}_units`] = pickUnits(figures, basis, count);
        }
        return entry;
      });
    },
    enabled: tab === "chart",
    staleTime: 5 * 60_000,
  });

  const periods = useMemo(
    () => periodsForYear(Number(breakdownYear), periodType),
    [breakdownYear, periodType],
  );

  const breakdownQuery = useQuery({
    queryKey: ["sales-analysis", "breakdown", row.id, breakdownYear, periodType],
    queryFn: async (): Promise<BreakdownRow[]> => {
      const results = await Promise.all(
        periods.map(async (entry) => {
          const data = await aggregate(entry.period);
          const breakdown = marginBreakdown(data, rate);
          return {
            ...breakdown,
            key: entry.key,
            label: entry.label,
            hasSales: Boolean(data.sales),
            grossMarginPercent: breakdown.sales === 0 ? 0 : breakdown.grossMargin / breakdown.sales,
          };
        }),
      );
      return results;
    },
    enabled: tab === "breakdown" && rateQuery.isSuccess,
    staleTime: 5 * 60_000,
  });

  // Both sides of every comparison, so the product tab can show the same
  // movement the grid does rather than bare figures.
  const windows = useMemo(
    () => columns.flatMap((column) => [column.current, column.compare]),
    [columns],
  );

  const productsQuery = useQuery({
    queryKey: ["sales-analysis", "client-products", row.id],
    queryFn: async () => {
      const parts = new Map<number, ProductRow>();
      const totals: Record<string, number> = {};

      const perWindow = await Promise.all(
        windows.map(async (window) => ({ window, data: await aggregate(window.period) })),
      );

      for (const { window, data } of perWindow) {
        totals[window.key] = data.sales?.quantity ?? 0;
        // Order type 1 is a sale; refunds and "other" would double-count.
        for (const part of data.parts ?? []) {
          if (part.totals?.order_type !== 1) continue;
          const id = part.part?.id ?? part.part_id;
          if (id === undefined) continue;
          const entry =
            parts.get(id) ??
            parts
              .set(id, {
                id,
                no: part.part?.no ?? String(id),
                description: part.part?.description1 ?? "",
                values: {},
              })
              .get(id)!;
          // Billed, like the chart tab beside it and the product editor's
          // sales tab. Per-product rows cannot show net — returns are booked
          // against generic parts — so the choice was billed or billed less
          // discount, and this was the last figure in the app on the latter.
          entry.values[window.key] = {
            money: billedOf(part.totals),
            units: part.totals?.quantity ?? 0,
          };
        }
      }

      return { rows: [...parts.values()], totals };
    },
    enabled: tab === "products",
    staleTime: 5 * 60_000,
  });

  const breakdownColumns = useMemo(() => {
    const c = defineColumns<BreakdownRow>();
    const money = (id: string, headerKey: string, pick: (line: BreakdownRow) => number) =>
      c.custom(id, pick, (line) => withCommas(pick(line)), {
        headerKey,
        filter: "range",
        align: "right",
      });

    /** Money over the period with its percentage of sales underneath. */
    const marginColumn = (
      id: string,
      headerKey: string,
      amount: (line: BreakdownRow) => number,
      share: (line: BreakdownRow) => number,
    ) =>
      c.custom(
        id,
        amount,
        (line) => (
          <span className="flex flex-col leading-tight">
            <span className="tabular-nums">{withCommas(amount(line))}</span>
            <span className="text-[12px] text-ink-muted tabular-nums">{percent(share(line))}</span>
          </span>
        ),
        { headerKey, filter: "none", align: "right" },
      );

    return [
      c.text("period", (line) => (line.hasSales ? line.label : t("analysis.no-sales", "no sales")), {
        headerKey: "column.period",
        enableSorting: false,
        priority: 1,
      }),
      c.number("quantity", (line) => line.units, {
        headerKey: "column.quantity",
        filter: "range",
        priority: 3,
      }),
      money("sales", "column.sales", (line) => line.sales),
      money("discounts", "margin.discounts", (line) => line.discounts),
      money("returns", "margin.returns", (line) => line.returns),
      money("programs", "margin.programs", (line) => line.programs),
      money("shipping_cost", "margin.shipping", (line) => line.shippingCost),
      money("advertising_cost", "margin.advertising", (line) => line.advertisingCost),
      money("commissions", "margin.commissions", (line) => line.commissions),
      money("royalties", "margin.royalties", (line) => line.royalties),
      money("cogs", "column.cogs", (line) => line.cogs),
      money("total_cost", "margin.total-cost", (line) => line.totalCost),
      marginColumn(
        "gross_margin",
        "margin.gross",
        (line) => line.grossMargin,
        (line) => line.grossMarginPercent,
      ),
      marginColumn(
        "net_margin",
        "margin.net",
        (line) => line.netMargin,
        (line) => line.netMarginPercent,
      ),
    ];
  }, [t]);

  const productColumns = useMemo(() => {
    const c = defineColumns<ProductRow>();
    const totals = productsQuery.data?.totals ?? {};

    /** This SKU's unit share of everything the client bought in the window. */
    const share = (line: ProductRow, key: string) => {
      const units = line.values[key]?.units ?? 0;
      const total = totals[key] ?? 0;
      return total === 0 ? 0 : units / total;
    };

    // The same three periods the grid shows, each carrying its own movement.
    const periodCols = columns.map((column) =>
      c.custom(
        column.key,
        (line) => line.values[column.current.key]?.money ?? 0,
        (line) => {
          const current = line.values[column.current.key] ?? EMPTY_PRODUCT_CELL;
          const previous = line.values[column.compare.key] ?? EMPTY_PRODUCT_CELL;
          // Share is the third line of the cell rather than a column of its
          // own, so it belongs to the window it describes — the two share
          // columns that used to sit at the end were pinned to "this month"
          // and "this year" whatever the grid was showing.
          return (
            <span className="grid grid-cols-[1fr_auto] items-baseline gap-x-3 gap-y-0.5 leading-tight">
              <span className="tabular-nums font-medium text-ink">{withCommas(current.money)}</span>
              <Movement value={ratio(current.money, previous.money)} />
              <span className="tabular-nums text-[12px] text-ink-muted">{`${current.units} u`}</span>
              <Movement value={ratio(current.units, previous.units)} small />
              <span className="tabular-nums text-[11px] text-ink-faint">
                {t("analysis.share.of-client", "{{share}} of client", { share: percent(share(line, column.current.key)) })}
              </span>
            </span>
          );
        },
        {
          header: `${column.current.label} vs ${column.compare.label}`,
          filter: "range",
          align: "right",
        },
      ),
    );

    return [
      c.custom(
        "product",
        (line) => `${line.no} ${line.description}`,
        (line) => (
          <span className="flex flex-col leading-tight">
            <span className="text-ink">{line.no}</span>
            <span className="text-[11px] text-ink-muted">{line.description}</span>
          </span>
        ),
        { headerKey: "column.product", priority: 1 },
      ),
      ...periodCols,
    ];
  }, [windows, productsQuery.data, t]);

  const yearOptions = Array.from({ length: 6 }, (_, index) => String(currentYear - index)).map(
    (value) => ({ value, label: value }),
  );

  // The categorical chart slots, in their fixed order — status tokens are
  // reserved for status and must not double as series colours.
  const palette = [
    "var(--chart-1)",
    "var(--chart-2)",
    "var(--chart-3)",
    "var(--chart-4)",
    "var(--chart-5)",
    "var(--chart-6)",
  ];

  /**
   * The cost decomposition for each of the three periods.
   *
   * Deliberately the same windows the grid behind it is showing, so the net
   * margin here is the same number the user just clicked on rather than a
   * differently-bounded one that happens to be nearby.
   */
  const marginQuery = useQuery({
    queryKey: [
      "sales-analysis",
      "client-margin",
      row.id,
      row.synthetic ?? false,
      columns.map((column) => column.current.key).join(","),
    ],
    queryFn: async () => {
      const entries = await Promise.all(
        columns.map(async (column) => ({
          key: column.key,
          label: column.current.label,
          breakdown: marginBreakdown(await aggregate(column.current.period), rate),
        })),
      );
      return entries;
    },
    enabled: tab === "margin" && canSeeMargin && rateQuery.isSuccess,
    staleTime: 5 * 60_000,
  });

  const tabs = [
    { key: "chart" as const, label: t("analysis.chart", "Sales chart") },
    { key: "breakdown" as const, label: t("tab.sales.analysis-grid", "Sales grid") },
    { key: "products" as const, label: t("analysis.products", "Products") },
    ...(canSeeMargin
      ? [{ key: "margin" as const, label: t("analysis.margin", "Margin") }]
      : []),
  ];

  return (
    <Dialog
      open
      onClose={onClose}
      title={row.name}
      description={row.currency}
      size="xl"
      actions={
        <Button variant="secondary" onClick={onClose}>
          {t("button.close", "Close")}
        </Button>
      }
    >
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="flex rounded-md border border-line p-0.5">
          {tabs.map((option) => (
            <button
              key={option.key}
              type="button"
              onClick={() => setTab(option.key)}
              className={
                tab === option.key
                  ? "rounded bg-accent-soft px-3 py-1 text-[13px] font-medium text-accent"
                  : "rounded px-3 py-1 text-[13px] text-ink-muted hover:text-ink"
              }
            >
              {option.label}
            </button>
          ))}
        </div>

        {tab === "chart" && (
          <>
            <div className="w-52">
              <MultiSelect
                options={yearOptions}
                value={years}
                onChange={(next: string[]) => {
                  if (next.length > 0) setYears(next);
                }}
              />
            </div>
            <Select
              value={mode}
              options={[
                { value: "money", label: t("product.sales.money", "Money") },
                { value: "units", label: t("product.sales.units", "Units") },
              ]}
              onChange={(event) => setMode(event.target.value as "money" | "units")}
              className="ml-auto w-28"
            />
          </>
        )}

        {tab === "breakdown" && (
          <>
            <Select
              value={breakdownYear}
              options={yearOptions}
              onChange={(event) => setBreakdownYear(event.target.value)}
              className="w-28"
            />
            <Select
              value={periodType}
              options={[
                { value: "quarter", label: t("analysis.period.quarter", "Quarter") },
                { value: "month", label: t("analysis.period.month", "Month") },
                { value: "week", label: t("analysis.period.week", "Week") },
              ]}
              onChange={(event) => setPeriodType(event.target.value as PeriodType)}
              className="w-32"
            />
          </>
        )}
      </div>

      {tab === "chart" &&
        (chartQuery.isLoading ? (
          <div className="flex h-64 items-center justify-center">
            <span className="size-5 animate-spin rounded-full border-2 border-accent border-t-transparent" />
          </div>
        ) : (
          <SeriesBarChart
            data={chartQuery.data ?? []}
            xKey="month"
            series={[...years].sort().map((year, index) => ({
              key: `y${year}_${mode}`,
              label: year,
              color: palette[index % palette.length] as string,
            }))}
            formatValue={(value) =>
              mode === "money"
                ? new Intl.NumberFormat(undefined, { notation: "compact" }).format(value)
                : new Intl.NumberFormat().format(value)
            }
          />
        ))}

      {tab === "breakdown" && (
        <div className="flex h-[60vh] flex-col">
          <DataTable
            data={breakdownQuery.data ?? []}
            columns={breakdownColumns}
            getRowId={(line) => line.key}
            loading={breakdownQuery.isLoading}
            emptyMessage={t("table.empty", "Nothing to show")}
          />
        </div>
      )}

      {tab === "products" && (
        <div className="flex h-[60vh] flex-col">
          <DataTable
            data={productsQuery.data?.rows ?? []}
            columns={productColumns}
            getRowId={(line) => String(line.id)}
            loading={productsQuery.isLoading}
            initialSorting={[{ id: "prev_year", desc: true }]}
            emptyMessage={t("table.empty", "Nothing to show")}
          />
        </div>
      )}

      {tab === "margin" && (
        <MarginPanel entries={marginQuery.data ?? []} loading={marginQuery.isLoading} />
      )}
    </Dialog>
  );
}

/** One line of the decomposition, and how to read it. */
type MarginLine = {
  key: keyof MarginBreakdown;
  labelKey: string;
  fallback: string;
  /** A cost subtracts from margin, so it reads better shown as a deduction. */
  cost?: boolean;
  /** Sub-total or total: given weight, and a rule above it. */
  total?: boolean;
  percent?: boolean;
};

const MARGIN_LINES: MarginLine[] = [
  { key: "units", labelKey: "margin.units", fallback: "Units" },
  { key: "sales", labelKey: "margin.sales", fallback: "Sales" },
  { key: "discounts", labelKey: "margin.discounts", fallback: "Discounts", cost: true },
  { key: "returns", labelKey: "margin.returns", fallback: "Returns", cost: true },
  { key: "totalPrograms", labelKey: "margin.total-programs", fallback: "Programs", cost: true },
  { key: "shippingCost", labelKey: "margin.shipping", fallback: "Shipping", cost: true },
  { key: "advertisingCost", labelKey: "margin.advertising", fallback: "Advertising", cost: true },
  { key: "commissions", labelKey: "margin.commissions", fallback: "Commissions", cost: true },
  { key: "royalties", labelKey: "margin.royalties", fallback: "Royalties", cost: true },
  { key: "cogs", labelKey: "column.cogs", fallback: "COGS", cost: true },
  { key: "totalCost", labelKey: "margin.total-cost", fallback: "Total cost", cost: true, total: true },
  { key: "grossMargin", labelKey: "margin.gross", fallback: "Gross margin", total: true },
  { key: "netMargin", labelKey: "margin.net", fallback: "Net margin", total: true },
  { key: "netMarginPercent", labelKey: "margin.net-percent", fallback: "Net margin %", percent: true, total: true },
];

/**
 * Where this client's margin went, over the same three periods as the grid.
 *
 * The margin page answers this across every client at once, which is how you
 * find who has a problem. This answers it for one, which is what you want the
 * moment you have found them, and it saves the trip of leaving the screen and
 * setting a period picker to something comparable.
 */
function MarginPanel({
  entries,
  loading,
}: {
  entries: Array<{ key: string; label: string; breakdown: MarginBreakdown }>;
  loading: boolean;
}) {
  const { t } = useTranslation();

  if (loading) {
    return <p className="p-4 text-[13px] text-ink-faint">{t("common.loading", "Loading...")}</p>;
  }
  if (entries.length === 0) {
    return <p className="p-4 text-[13px] text-ink-faint">{t("table.empty", "Nothing to show")}</p>;
  }

  return (
    <div className="max-h-[60vh] overflow-auto">
      <table className="w-full text-[13px]">
        <thead className="sticky top-0 bg-surface">
          <tr className="text-left text-[11px] uppercase tracking-wider text-ink-faint">
            <th className="py-2 pr-3 font-medium">{t("analysis.margin.line", "Line")}</th>
            {entries.map((entry) => (
              <th key={entry.key} className="py-2 pl-3 text-right font-medium">
                {entry.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {MARGIN_LINES.map((line) => (
            <tr
              key={line.key}
              className={line.total ? "border-t border-line" : "border-t border-line-soft"}
            >
              <td
                className={
                  line.total
                    ? "py-1.5 pr-3 font-medium text-ink"
                    : "py-1.5 pr-3 text-ink-muted"
                }
              >
                {t(line.labelKey, line.fallback)}
              </td>
              {entries.map((entry) => {
                const value = entry.breakdown[line.key] as number;
                return (
                  <td
                    key={entry.key}
                    className={cn(
                      "tabular-nums py-1.5 pl-3 text-right",
                      line.total ? "font-medium text-ink" : "text-ink",
                      line.cost && value !== 0 && "text-risk",
                    )}
                  >
                    {line.percent
                      ? percent(value)
                      : line.key === "units"
                        ? value
                        : withCommas(value)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
